import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { StateAssociationService } from "../../../services/state.association.service";
import { StateAssociation } from "../../../models/state.association.model";
import { NgClass, NgForOf, NgIf } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Agency } from "../../../models/agency.model";
import { ToastService } from "../../../services/toast.service";
import { AgencyService } from "../../../services/agency.service";
import { Page } from "../../../models/page.model";
import {
  Subject,
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  EMPTY,
  forkJoin,
  firstValueFrom,
  of
} from "rxjs";
import { map, catchError } from "rxjs/operators";
import { CdkTableModule } from "@angular/cdk/table";
import { StateManagementService } from "../../../services/state-management.service";
import { AuthGuard } from "../../../auth/auth.guard";
import { DragDropZoneComponent } from "../drag-and-drop/drag-and-drop.component";

interface UploadResult {
  totalCount: number;
  successCount: number;
  duplicateCount: number;
  invalidCount: number;
  invalidIds: string[];
}

@Component({
  selector: "app-state-association",
  templateUrl: "./state.association.component.html",
  standalone: true,
  imports: [NgClass, FormsModule, RouterLink, NgIf, CdkTableModule, NgForOf, DragDropZoneComponent],
  styleUrls: ["./state.association.component.scss"]
})
export class StateAssociationComponent implements OnInit {
  // CDK Table columns
  displayedColumns: string[] = ["name", "agencyId", "subscription", "location", "actions"];

  agencies?: Agency[];
  filteredAgencies: Agency[] = [];
  searchTerm = "";
  selectedStateAssociation?: StateAssociation;

  isEditModalOpen = false;
  editMemberLevel?: number;
  editAssociationName?: string;
  editAssociationUrl?: string;
  editLogoFile?: File;
  editLogoUrl?: string;

  isAddAgencyModalOpen = false;
  searchAgencyId = "";
  agencyFound = false;
  agencyNotFound = false;
  selectedAgency?: Agency | undefined;

  isDeleteModalOpen = false;
  agencyToDelete?: Agency;
  found: Agency | undefined;

  // Bulk upload properties
  isBulkUploadModalOpen = false;
  bulkUploadFile?: File;
  bulkUploadFileName?: string;
  isProcessing = false;
  uploadResult?: UploadResult;
  showUploadSummary = false;

  isLoadingAgencies = false;

  // Sorting properties
  currentSortColumn?: string;
  currentSortDirection: "asc" | "desc" = "asc";

  logoImageUrl?: string;
  private onDestroy$: Subject<void> = new Subject<void>();
  private TOAST_DURATION = 3000;
  agencySearch$ = new Subject<string>();

  constructor(
    private route: ActivatedRoute,
    private stateAssociationService: StateAssociationService,
    private agencyService: AgencyService,
    private toastService: ToastService,
    private stateManagementService: StateManagementService,
    private authGuard: AuthGuard
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get("id"));
    this.stateManagementService.setPageTitle("State Associations | Momentum Edge");
    this.stateManagementService.pageHeading.set("State Associations");
    if (!this.authGuard.isAdmin()) {
      return;
    }
    this.isLoadingAgencies = true;

    this.stateAssociationService.getStateAssociation(id).subscribe({
      next: (assoc) => {
        if (assoc) {
          this.selectedStateAssociation = assoc;
          this.agencies = this.selectedStateAssociation.agencies?.sort((a, b) =>
            (a.name || "").localeCompare(b.name || "")
          );
          this.filteredAgencies = this.agencies || [];
          this.loadLogo(assoc.id!);
        }
        this.isLoadingAgencies = false;
      }
    });

    this.agencySearch$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((value) => {
          const numericId = Number(value);

          if (!numericId) {
            this.agencyFound = false;
            this.selectedAgency = undefined;
            return EMPTY;
          }

          return this.agencyService.getAgency(numericId);
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe({
        next: (agencyPage: Page<Agency>) => {
          if (agencyPage.content.length > 0) {
            this.agencyFound = true;
            this.selectedAgency = agencyPage.content[0];
          } else {
            this.agencyFound = false;
            this.selectedAgency = undefined;
          }
        },
        error: () => {
          this.agencyFound = false;
          this.selectedAgency = undefined;
        }
      });
  }

  loadLogo(stateAssociationId: number): void {
    this.stateAssociationService.getStateAssociationImage(stateAssociationId).subscribe({
      next: (blob) => {
        const reader = new FileReader();
        reader.onload = () => {
          this.logoImageUrl = reader.result as string;
        };
        reader.readAsDataURL(blob);
      },
      error: () => {
        this.logoImageUrl = undefined;
      }
    });
  }

  filterAgencies() {
    if (this.agencies) {
      const term = this.searchTerm?.trim().toLowerCase() || "";
      this.filteredAgencies = this.agencies.filter((a) => a.name?.toLowerCase().includes(term));
    } else {
      this.filteredAgencies = [];
    }
  }

  // Helper method for subscription display
  getSubscriptionPlan(agency: Agency): string {
    return agency.subscriptions?.[0]?.plan?.name || "No subscription";
  }

  // Helper method for location display
  getLocation(agency: Agency): string {
    const city = agency.address?.city || "";
    const state = agency.address?.state || agency.address?.regionCode || "";
    if (city && state) {
      return `${city}, ${state}`;
    }
    return city || state || "N/A";
  }

  // Sorting methods
  sortByColumn(column: string) {
    if (this.currentSortColumn === column) {
      // Toggle direction if same column
      this.currentSortDirection = this.currentSortDirection === "asc" ? "desc" : "asc";
    } else {
      // New column, default to ascending
      this.currentSortColumn = column;
      this.currentSortDirection = "asc";
    }

    this.applySorting();
  }

  private applySorting() {
    if (!this.currentSortColumn || !this.filteredAgencies) return;

    this.filteredAgencies = [...this.filteredAgencies].sort((a, b) => {
      let compareValue = 0;

      switch (this.currentSortColumn) {
        case "name":
          compareValue = (a.name || "").localeCompare(b.name || "");
          break;
        case "agencyId":
          const idA = Number(a.agencyId) || 0;
          const idB = Number(b.agencyId) || 0;
          compareValue = idA - idB;
          break;
        case "subscription":
          const subA = this.getSubscriptionPlan(a);
          const subB = this.getSubscriptionPlan(b);
          compareValue = subA.localeCompare(subB);
          break;
        case "location":
          const locA = this.getLocation(a);
          const locB = this.getLocation(b);
          compareValue = locA.localeCompare(locB);
          break;
      }

      return this.currentSortDirection === "asc" ? compareValue : -compareValue;
    });
  }

  getSortIndicator(column: string): string {
    if (this.currentSortColumn !== column) return "";
    return this.currentSortDirection === "asc" ? "↑" : "↓";
  }

  editDetails() {
    if (!this.selectedStateAssociation) return;
    // Initialize modal fields with current data
    this.editMemberLevel = this.selectedStateAssociation.memberLevel;
    this.editAssociationName = this.selectedStateAssociation.name;
    this.editAssociationUrl = this.selectedStateAssociation.url;
    this.isEditModalOpen = true;
  }

  cancelEdit() {
    this.isEditModalOpen = false;
    this.editMemberLevel = undefined;
    this.editAssociationName = undefined;
    this.editAssociationUrl = undefined;
    this.editLogoFile = undefined;
    this.editLogoUrl = undefined;
  }

  onBackdropClick(event: Event) {
    // Only close if clicking directly on backdrop, not on modal content
    if (event.target === event.currentTarget) {
      this.cancelEdit();
    }
  }

  // NEW: Handler for logo file from drag-drop-zone component
  handleLogoFile(file: File): void {
    if (file.type === "image/png" || file.type === "image/jpeg") {
      this.editLogoFile = file;

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.editLogoUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      this.toastService.open("Please upload a PNG or JPG file.", "Close", { duration: 3000 });
    }
  }

  saveChanges() {
    if (!this.selectedStateAssociation) return;

    // Convert to number explicitly to ensure proper comparison in ngClass
    const newMemberLevel = Number(this.editMemberLevel);

    if (this.editLogoFile && this.editLogoUrl) {
      const base64Data = this.editLogoUrl.split(",")[1];

      this.stateAssociationService
        .uploadStateAssociationImage(this.selectedStateAssociation.id!, base64Data)
        .subscribe({
          next: () => {
            this.loadLogo(this.selectedStateAssociation!.id!);
            // Create a new object reference with proper number type
            this.selectedStateAssociation = {
              ...this.selectedStateAssociation!,
              memberLevel: newMemberLevel,
              name: this.editAssociationName,
              url: this.editAssociationUrl
            };
            this.updateStateAssociation();
          },
          error: () => {
            this.toastService.open("Failed to upload image. Please try again.", "Close", {
              duration: 3000
            });
          }
        });
    } else {
      // Create a new object reference with proper number type
      this.selectedStateAssociation = {
        ...this.selectedStateAssociation,
        memberLevel: newMemberLevel,
        name: this.editAssociationName,
        url: this.editAssociationUrl
      };
      this.updateStateAssociation();
    }
  }

  private updateStateAssociation() {
    if (!this.selectedStateAssociation) return;

    this.stateAssociationService.updateStateAssociation(this.selectedStateAssociation).subscribe({
      next: () => {
        this.isEditModalOpen = false;
        this.editLogoFile = undefined;
        this.toastService.open(
          `Updates to state association [ ${this.selectedStateAssociation?.state} ] has been saved successfully.`,
          "Close",
          { duration: 3000 }
        );
      },
      error: () => {
        this.toastService.open("Failed to update state association. Please try again.", "Close", {
          duration: 3000
        });
      }
    });
  }

  addAgency() {
    this.isAddAgencyModalOpen = true;
    this.searchAgencyId = "";
    this.agencyFound = false;
    this.agencyNotFound = false;
    this.selectedAgency = undefined;
  }

  confirmAddAgency() {
    if (!this.selectedAgency || !this.selectedStateAssociation) {
      this.toastService.open(
        `Agency [ ${this.selectedAgency?.name} ] could not be added to state association.`,
        "Close",
        {
          duration: 3000
        }
      );
      return;
    }
    if (!this.selectedAgency.fulcrumId || !this.selectedStateAssociation.id) {
      this.toastService.open(
        `Agency [ ${this.selectedAgency?.name} ] could not be added to state association.`,
        "Close",
        {
          duration: 3000
        }
      );
      return;
    }
    const duplicateAgency = this.agencies?.find(
      (agency) => agency.agencyId === this.selectedAgency!.fulcrumId
    );

    if (duplicateAgency) {
      console.log("duplicate");
      this.toastService.open(`Agency is already a member of the state association.`, "Close", {
        duration: 3000
      });
      return;
    }
    this.stateAssociationService
      .addAgencyToStateAssociation(this.selectedStateAssociation.id, this.selectedAgency.fulcrumId)
      .subscribe({
        next: () => {
          this.toastService.open(
            `Agency [ ${this.selectedAgency?.name} ] has been added to state association.`,
            "Close",
            {
              duration: 3000
            }
          );

          if (!this.agencies) {
            this.agencies = [];
          }
          this.selectedAgency!.agencyId = this.selectedAgency?.fulcrumId;
          this.agencies.push(this.selectedAgency!);
          this.filteredAgencies = [...this.agencies];
          this.cancelAddAgency();
        }
      });
  }

  cancelAddAgency() {
    this.isAddAgencyModalOpen = false;
    this.searchAgencyId = "";
    this.agencyFound = false;
    this.agencyNotFound = false;
    this.selectedAgency = undefined;
  }

  onAddAgencyBackdropClick(event: Event) {
    if (event.target === event.currentTarget) {
      this.cancelAddAgency();
    }
  }

  deleteAgency(agency: Agency) {
    this.agencyToDelete = agency;
    this.isDeleteModalOpen = true;
  }

  confirmDelete() {
    if (!this.agencyToDelete || !this.selectedStateAssociation) return;
    if (!this.agencyToDelete.agencyId || !this.selectedStateAssociation.id) return;

    this.stateAssociationService
      .removeAgencyFromStateAssociation(this.agencyToDelete.agencyId)
      .subscribe({
        next: () => {
          this.toastService.open(`Agency has been removed from state association.`, "Close", {
            duration: 3000
          });

          // Remove from local arrays
          this.agencies = this.agencies?.filter(
            (a) => a.agencyId !== this.agencyToDelete!.agencyId
          );
          this.filteredAgencies = [...(this.agencies || [])];
          this.cancelDelete();
        },
        error: (err) => {
          alert("Failed to remove agency. Please try again.");
        }
      });
  }

  cancelDelete() {
    this.isDeleteModalOpen = false;
    this.agencyToDelete = undefined;
  }

  onDeleteBackdropClick(event: Event) {
    if (event.target === event.currentTarget) {
      this.cancelDelete();
    }
  }

  // Bulk Upload Methods
  openBulkUploadModal() {
    this.isBulkUploadModalOpen = true;
    this.bulkUploadFile = undefined;
    this.bulkUploadFileName = undefined;
    this.uploadResult = undefined;
    this.showUploadSummary = false;
  }

  cancelBulkUpload() {
    this.isBulkUploadModalOpen = false;
    this.bulkUploadFile = undefined;
    this.bulkUploadFileName = undefined;
    this.uploadResult = undefined;
    this.showUploadSummary = false;
    this.isProcessing = false;
  }

  onBulkUploadBackdropClick(event: Event) {
    if (event.target === event.currentTarget && !this.isProcessing) {
      this.cancelBulkUpload();
    }
  }

  handleBulkFile(file: File): void {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (fileExtension !== "csv" && fileExtension !== "xlsx" && fileExtension !== "xls") {
      this.toastService.open(
        "Unsupported file format. Please upload a CSV or XLSX file.",
        "Close",
        { duration: this.TOAST_DURATION }
      );
      return;
    }

    this.bulkUploadFile = file;
    this.bulkUploadFileName = file.name;
  }

  downloadTemplate() {
    // Create CSV content
    const csvContent = "AgencyID\n001247\n002458\n003691";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "agency_upload_template.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  }

  async processBulkUpload() {
    if (!this.bulkUploadFile || !this.selectedStateAssociation) return;

    this.isProcessing = true;
    this.showUploadSummary = false;

    try {
      const agencyIds = await this.parseFile(this.bulkUploadFile);

      if (agencyIds.length === 0) {
        this.toastService.open(
          "No agency IDs found in file. Please check the file format.",
          "Close",
          { duration: this.TOAST_DURATION }
        );
        this.isProcessing = false;
        return;
      }

      const result: UploadResult = {
        totalCount: agencyIds.length,
        successCount: 0,
        duplicateCount: 0,
        invalidCount: 0,
        invalidIds: []
      };

      const nonDuplicates: string[] = [];
      for (const agencyId of agencyIds) {
        if (this.agencies?.some((a) => a.agencyId?.toString() === agencyId)) {
          result.duplicateCount++;
        } else {
          nonDuplicates.push(agencyId);
        }
      }

      const BATCH_SIZE = 10;
      for (let i = 0; i < nonDuplicates.length; i += BATCH_SIZE) {
        const batch = nonDuplicates.slice(i, i + BATCH_SIZE);
        const batchResults = await firstValueFrom(
          forkJoin(
            batch.map((agencyId) =>
              this.stateAssociationService
                .addAgencyToStateAssociation(this.selectedStateAssociation!.id!, parseInt(agencyId))
                .pipe(
                  map(() => ({ agencyId, success: true, error: null as any })),
                  catchError((err) => of({ agencyId, success: false, error: err }))
                )
            )
          )
        );

        for (const r of batchResults) {
          if (r.success) {
            result.successCount++;
          } else {
            result.invalidCount++;
            result.invalidIds.push(r.agencyId);
          }
        }
      }

      this.uploadResult = result;
      this.showUploadSummary = true;

      if (this.selectedStateAssociation.id) {
        this.isLoadingAgencies = true;
        this.stateAssociationService
          .getStateAssociationById(this.selectedStateAssociation.id)
          .subscribe({
            next: (assoc) => {
              if (assoc) {
                this.agencies = assoc.agencies?.sort((a, b) =>
                  (a.name || "").localeCompare(b.name || "")
                );
                this.filteredAgencies = this.agencies || [];
              }
              this.isLoadingAgencies = false;
            }
          });
      }
    } catch (error: any) {
      this.toastService.open(
        error.message || "Failed to process file. Please try again.",
        "Close",
        { duration: this.TOAST_DURATION }
      );
    } finally {
      this.isProcessing = false;
    }
  }

  private async parseFile(file: File): Promise<string[]> {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (fileExtension === "csv") {
      return this.parseCSV(file);
    } else if (fileExtension === "xlsx" || fileExtension === "xls") {
      return this.parseXLSX(file);
    }

    throw new Error("Unsupported file format");
  }

  private async parseCSV(file: File): Promise<string[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const text = e.target.result;
        const lines = text
          .split("\n")
          .map((line: string) => line.trim())
          .filter((line: string) => line);

        if (lines.length === 0) {
          reject(new Error("File is empty"));
          return;
        }

        const header = lines[0].toLowerCase();

        // Check if AgencyID column exists (case-insensitive)
        if (!header.includes("agencyid")) {
          reject(
            new Error("AgencyID column not found. Please upload a file with an AgencyID column.")
          );
          return;
        }

        // Extract agency IDs (skip header)
        const agencyIds = lines
          .slice(1)
          .map((line: string) => {
            // Handle CSV with multiple columns - take first column
            const columns = line.split(",");
            return columns[0].trim();
          })
          .filter((id: string | any[]) => id && id.length > 0);

        resolve(agencyIds);
      };

      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(file);
    });
  }

  private async parseXLSX(file: File): Promise<string[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (e: any) => {
        try {
          // Dynamically import XLSX library
          const XLSX = await import("xlsx");
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          // Get first sheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

          if (jsonData.length === 0) {
            reject(new Error("File is empty"));
            return;
          }

          const header = jsonData[0].map((col: any) => String(col).toLowerCase());
          const agencyIdIndex = header.findIndex((col) => col.includes("agencyid"));

          if (agencyIdIndex === -1) {
            reject(
              new Error("AgencyID column not found. Please upload a file with an AgencyID column.")
            );
            return;
          }

          // Extract agency IDs (skip header)
          const agencyIds = jsonData
            .slice(1)
            .map((row) => String(row[agencyIdIndex] || "").trim())
            .filter((id) => id && id.length > 0);

          resolve(agencyIds);
        } catch (error) {
          reject(new Error("Failed to parse XLSX file"));
        }
      };

      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(file);
    });
  }
}
